/**
 * articleText — render-time defense against publishing artifacts.
 *
 * Articles in Memory Lane come from two places:
 *   - Static seoArticles*.js
 *   - The Admin Content Studio (MongoDB studio_articles)
 *
 * The Content Studio's "Apply suggested fixes" pass inserts markers
 * like [INTERNAL LINK: /group/slug] for Ashlee to resolve before
 * publishing. If she ever forgets, OR if a draft from BabyLoveGrowth
 * sneaks through with leftover [CITATION NEEDED], [SOURCE], TODO,
 * Lorem ipsum, or unrendered Markdown (**bold**, ## Heading), this
 * module strips/repairs it before the reader ever sees it.
 *
 * Two exports:
 *   - cleanText(s):  pure string sanitizer (removes the artifacts,
 *                    flattens markdown to plain text). Use for places
 *                    that can't render React, e.g. og:description,
 *                    JSON-LD payloads.
 *   - renderText(s, slugIndex):  returns an array of React nodes with
 *                    [INTERNAL LINK: /…] replaced by a <Link> when the
 *                    destination is known, **bold** rendered as
 *                    <strong>, *italic* as <em>. Anything that can't
 *                    be resolved is stripped — never shown raw.
 */
import React from "react";
import { Link } from "react-router-dom";

// ---------------------------------------------------------------------------
// Patterns we strip OR rewrite
// ---------------------------------------------------------------------------
// [INTERNAL LINK: /group/slug]  →  <Link> (or stripped if unknown)
const INTERNAL_LINK_RE = /\[INTERNAL LINK:\s*([^\]]+)\]/g;
// Strip-only markers. Whitespace before/after is also pulled so we don't
// leave double-spaces or stranded punctuation.
const STRIP_RE = /\s*\[(?:CITATION(?:\s+NEEDED)?|SOURCE|TODO|FIXME)\]\s*/gi;
const LOREM_RE = /\s*\bLorem ipsum[^.]*\.?\s*/gi;
// Standalone "TODO:" or "TODO " at start of a sentence.
const TODO_LINE_RE = /(?:^|\s)TODO[:\s]+[^.!?\n]*[.!?\n]?/g;
// Unrendered markdown headings (## Heading at line start). We strip the
// hashes since a heading inside a paragraph would be ugly — the editor
// should have used a "h2" block.
const MD_HEADING_RE = /(^|\n)\s*#{1,6}\s+/g;
// Bold / italic / inline code. We render bold/italic into React; code
// blocks just stay as plain text inside <code>.
const MD_BOLD_RE = /\*\*([^*\n]+?)\*\*/g;
const MD_ITALIC_RE = /(^|[\s(])\*([^*\n]+?)\*(?=[\s.,!?)]|$)/g;
const MD_CODE_RE = /`([^`\n]+?)`/g;
// Collapse runs of whitespace introduced by stripping.
const WS_RE = /[ \t]{2,}/g;

// ---------------------------------------------------------------------------
// Public: cleanText — string-only sanitizer
// ---------------------------------------------------------------------------
export function cleanText(input) {
    if (input == null) return "";
    let s = String(input);
    s = s.replace(INTERNAL_LINK_RE, ""); // strip markers in plain-text contexts
    s = s.replace(STRIP_RE, " ");
    s = s.replace(LOREM_RE, " ");
    s = s.replace(TODO_LINE_RE, " ");
    s = s.replace(MD_HEADING_RE, "$1");
    s = s.replace(MD_BOLD_RE, "$1");
    s = s.replace(MD_ITALIC_RE, "$1$2");
    s = s.replace(MD_CODE_RE, "$1");
    s = s.replace(WS_RE, " ").replace(/\s+\n/g, "\n").trim();
    return s;
}

// ---------------------------------------------------------------------------
// slugIndex — build a fast { "/group/slug" → {h1, group, slug} } map.
// ---------------------------------------------------------------------------
export function buildSlugIndex(allArticles) {
    const idx = new Map();
    if (!Array.isArray(allArticles)) return idx;
    for (const a of allArticles) {
        if (!a || !a.slug || !a.group) continue;
        idx.set(`/${a.group}/${a.slug}`, { h1: a.h1 || a.title || a.slug, group: a.group, slug: a.slug });
    }
    return idx;
}

// ---------------------------------------------------------------------------
// Internal: render bold/italic/code inside a plain text segment.
// Returns an array of React nodes.
// ---------------------------------------------------------------------------
function renderInlineMarkdown(text, keyPrefix) {
    if (!text) return [];
    // Pre-strip the artifacts so they never show up.
    let s = text
        .replace(STRIP_RE, " ")
        .replace(LOREM_RE, " ")
        .replace(TODO_LINE_RE, " ")
        .replace(MD_HEADING_RE, "$1")
        .replace(WS_RE, " ");

    const nodes = [];
    // Tokenize on **bold**, *italic*, `code` — we walk through once.
    // To keep this simple we use a unified regex that captures the marker
    // and the inner text, then split.
    const re = /(\*\*([^*\n]+?)\*\*|`([^`\n]+?)`|(^|[\s(])\*([^*\n]+?)\*(?=[\s.,!?)]|$))/g;
    let last = 0, m, i = 0;
    while ((m = re.exec(s)) !== null) {
        if (m.index > last) {
            nodes.push(s.slice(last, m.index));
        }
        if (m[2] != null) {
            nodes.push(<strong key={`${keyPrefix}-b-${i++}`}>{m[2]}</strong>);
        } else if (m[3] != null) {
            nodes.push(<code key={`${keyPrefix}-c-${i++}`} className="bg-cream-light px-1 rounded">{m[3]}</code>);
        } else if (m[5] != null) {
            // italic — preserve the leading whitespace/paren that the regex captured.
            if (m[4]) nodes.push(m[4]);
            nodes.push(<em key={`${keyPrefix}-i-${i++}`}>{m[5]}</em>);
        }
        last = re.lastIndex;
    }
    if (last < s.length) nodes.push(s.slice(last));
    return nodes;
}

// ---------------------------------------------------------------------------
// Public: renderText — full React-node renderer.
// ---------------------------------------------------------------------------
export function renderText(input, slugIndex, keyPrefix = "rt") {
    if (input == null) return null;
    const s = String(input);
    // Walk through INTERNAL LINK markers, splitting the string into
    // plain-text chunks and <Link> insertions.
    const out = [];
    let last = 0, m, i = 0;
    INTERNAL_LINK_RE.lastIndex = 0;
    while ((m = INTERNAL_LINK_RE.exec(s)) !== null) {
        // Plain-text segment before the marker.
        const before = s.slice(last, m.index);
        if (before) out.push(...renderInlineMarkdown(before, `${keyPrefix}-pre-${i}`));
        const targetRaw = (m[1] || "").trim();
        // Accept "/group/slug" or "group/slug" — normalize.
        const target = targetRaw.startsWith("/") ? targetRaw : `/${targetRaw}`;
        const found = slugIndex && slugIndex.get && slugIndex.get(target);
        if (found) {
            // Real clickable internal link with the destination article's H1.
            out.push(
                <Link
                    key={`${keyPrefix}-link-${i}`}
                    to={target}
                    className="text-teal-dark underline hover:text-teal"
                    data-testid={`article-inline-link-${found.slug}`}
                >
                    {found.h1}
                </Link>
            );
            // Eat one trailing space so we don't get "word  word" after the link.
            if (s[m.index + m[0].length] === " ") {
                INTERNAL_LINK_RE.lastIndex += 1;
            }
        } else {
            // Unknown destination → strip the marker entirely (incl. a
            // surrounding space so we don't leave a double-space).
            if (s[m.index + m[0].length] === " ") {
                INTERNAL_LINK_RE.lastIndex += 1;
            }
        }
        last = INTERNAL_LINK_RE.lastIndex;
        i += 1;
    }
    const tail = s.slice(last);
    if (tail) out.push(...renderInlineMarkdown(tail, `${keyPrefix}-tail`));
    return out.length === 0 ? null : out;
}

// ---------------------------------------------------------------------------
// Public: hasArtifacts — quick check used by admin scans.
// ---------------------------------------------------------------------------
export function hasArtifacts(input) {
    if (!input) return false;
    const s = String(input);
    // Use literal patterns (not the shared /g regexes) — calling .test() on
    // a /g regex advances its lastIndex and breaks the next .exec() call.
    return (
        /\[INTERNAL LINK:/i.test(s) ||
        /\[(?:CITATION(?:\s+NEEDED)?|SOURCE|TODO|FIXME)\]/i.test(s) ||
        /\bLorem ipsum\b/i.test(s) ||
        /\*\*[^*\n]+\*\*/.test(s) ||
        /(?:^|\n)\s*#{1,6}\s+\S/.test(s)
    );
}
